import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Episode } from '../types/episodes';
import { publicFileUrl } from '../lib/storage';
import Picture from '../components/Picture';
import MetaTags from '../components/MetaTags';
import { ExternalLink, Play, ArrowLeft, Gamepad2, ChevronDown, ArrowRight, ArrowDownLeft } from 'lucide-react';

interface EpisodePageProps {
  slug: string;
  onBack: () => void;
}

export default function EpisodePage({ slug, onBack }: EpisodePageProps) {
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blogMd, setBlogMd] = useState<string>('');
  const [embedHtml, setEmbedHtml] = useState<string>('');

  useEffect(() => {
    loadEpisode();
  }, [slug]);

  const loadEpisode = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('episodes')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!data) {
        setError('Episode not found');
        return;
      }
      setEpisode(data as Episode);
    } catch (err) {
      console.error('Error loading episode:', err);
      setError(err instanceof Error ? err.message : 'Failed to load episode');
    } finally {
      setLoading(false);
    }
  };

  const storageRoot = episode ? episode.storage_root : '';

  const urls = useMemo(() => {
    if (!episode) return {};
    return {
      blogMd: publicFileUrl(episode.storage_root, episode.blog_youtube_summary),
      embedHtml: publicFileUrl(episode.storage_root, episode.youtube_embed),
    } as const;
  }, [episode]);

  useEffect(() => {
    (async () => {
      if (urls && urls.blogMd) {
        try {
          const res = await fetch(urls.blogMd);
          if (res.ok) {
            setBlogMd(await res.text());
          }
        } catch {
          setBlogMd('');
        }
      } else {
        setBlogMd('');
      }

      if (urls && urls.embedHtml) {
        try {
          const res = await fetch(urls.embedHtml);
          if (res.ok) {
            let html = await res.text();
            html = html.replace(/width="\d+"/g, 'width="100%"');
            html = html.replace(/height="\d+"/g, 'height="100%"');
            html = html.replace(/frameborder="\d+"/g, '');
            setEmbedHtml(html);
          }
        } catch {
          setEmbedHtml('');
        }
      } else {
        setEmbedHtml('');
      }
    })();
  }, [urls]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-yellow-400 font-horizon text-2xl animate-pulse">Loading Episode...</div>
      </div>
    );
  }

  if (error || !episode) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-horizon text-4xl text-red-500 mb-4">{error || 'Episode Not Found'}</h1>
          <button
            onClick={onBack}
            className="bg-[#ffed00] text-black px-6 py-3 font-bold hover:bg-[#ffed00]/90 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const paragraphs = blogMd.split('\n\n').filter(p => p.trim());
  const thirdLength = Math.ceil(paragraphs.length / 3);
  const firstThird = paragraphs.slice(0, thirdLength);
  const secondThird = paragraphs.slice(thirdLength, thirdLength * 2);
  const thirdThird = paragraphs.slice(thirdLength * 2);

  const episodeDescription = blogMd.split('\n\n')[0]?.substring(0, 155) || episode.title;
  const episodeImage = episode.game_thumbnail_460x215
    ? publicFileUrl(episode.storage_root, episode.game_thumbnail_460x215)
    : 'https://yokzona.com/yok_logo_256.png';
  const episodeUrl = `https://yokzona.com/episodes/${slug}`;
  const episodeKeywords = episode.keywords?.join(', ') || undefined;

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <MetaTags
        title={`${episode.title} - ${episode.podcast_number} | yOKzONa`}
        description={episodeDescription}
        image={episodeImage}
        url={episodeUrl}
        type="article"
        keywords={episodeKeywords}
      />
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-red-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-red-600 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping animation-delay-1000"></div>
        <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-red-500 rounded-full animate-ping animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-yellow-400 rounded-full animate-ping animation-delay-3000"></div>
        <div className="absolute top-2/3 right-1/4 w-2 h-2 bg-red-500 rounded-full animate-ping animation-delay-4000"></div>
      </div>

      <div className="fixed top-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-sm border-b border-yellow-400/30 animate-slideDown">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 group transition-all duration-300 hover:scale-110"
          >
            <ArrowLeft className="group-hover:-translate-x-2 transition-transform duration-300" size={20} />
            <span className="font-bold">Back</span>
          </button>
          <div className="text-yellow-400 font-press-start text-xs animate-pulse">
            {episode.podcast_number}
          </div>
        </div>
      </div>

      <div className="pt-20 pb-16 relative z-10">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="mb-8 animate-fadeIn">
            <div className="inline-block bg-red-500 text-white px-4 py-2 font-bold text-sm mb-4 skew-x-[-5deg] shadow-lg hover:scale-110 transition-transform duration-300 hover:shadow-red-500/50 hover:shadow-2xl">
              <span className="inline-block skew-x-[5deg]">{episode.podcast_number}</span>
            </div>
            <h1 className="font-horizon text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6 animate-slideUp">
              {episode.title}
            </h1>
            <div className="flex flex-wrap gap-4 animate-slideUp animation-delay-200">
              {episode.youtube_link && (
                <a
                  href={episode.youtube_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 font-bold hover:bg-red-700 transition-all duration-300 hover:scale-110 transform shadow-xl hover:shadow-red-600/50 hover:shadow-2xl relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  <Play size={20} className="relative z-10 group-hover:scale-125 transition-transform" />
                  <span className="relative z-10">Watch Episode</span>
                </a>
              )}
              {episode.game_link && (
                <a
                  href={episode.game_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#ffed00] text-black px-6 py-3 font-bold hover:bg-[#ffed00]/90 transition-all duration-300 hover:scale-110 transform shadow-xl hover:shadow-yellow-400/50 hover:shadow-2xl relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  <Gamepad2 size={20} className="relative z-10 group-hover:rotate-12 transition-transform" />
                  <span className="relative z-10">Play The Game</span>
                </a>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-8">
              {embedHtml && (
                <div className="relative w-full group animate-fadeIn animation-delay-400">
                  <div
                    className="relative aspect-video rounded-lg overflow-hidden border-4 border-yellow-400/50 shadow-2xl shadow-yellow-400/30 transition-all duration-500 hover:border-yellow-400 hover:shadow-yellow-400/50 hover:scale-[1.02]"
                    dangerouslySetInnerHTML={{ __html: embedHtml }}
                  />
                </div>
              )}

              {firstThird.length > 0 && (
                <div className="relative">
                  <div className="bg-gradient-to-br from-red-900/30 to-black border-2 border-red-600/50 rounded-lg p-6 animate-fadeIn animation-delay-600 hover:border-red-600 transition-all duration-500 hover:shadow-lg hover:shadow-red-600/20 hover:scale-[1.01]">
                    <div className="text-gray-300 leading-relaxed space-y-4">
                      {firstThird.map((paragraph, idx) => (
                        <p key={idx} className="animate-fadeIn" style={{ animationDelay: `${800 + idx * 100}ms` }}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                  {secondThird.length > 0 && (
                    <div className="flex justify-end my-4 lg:block hidden">
                      <div className="flex items-center gap-2 animate-pulse">
                        <span className="text-yellow-400 text-xs font-bold tracking-wider">CONTINUE</span>
                        <ArrowRight className="text-yellow-400 animate-bounce" size={32} />
                      </div>
                    </div>
                  )}
                  {secondThird.length > 0 && (
                    <div className="flex justify-center my-4 lg:hidden">
                      <div className="flex flex-col items-center gap-2 animate-bounce">
                        <ChevronDown className="text-yellow-400" size={32} />
                        <span className="text-yellow-400 text-xs font-bold tracking-wider">CONTINUE READING</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {episode.screenshot_1_600x338 && (
                <div className="overflow-hidden rounded-lg border-2 border-red-500/50 animate-fadeIn animation-delay-1000 hover:border-red-500 transition-all duration-500 hover:shadow-lg hover:shadow-red-500/30 group">
                  <Picture
                    storageRoot={storageRoot}
                    filename={episode.screenshot_1_600x338}
                    alt="Screenshot 1"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              )}

              {thirdThird.length > 0 && (
                <div className="bg-gradient-to-br from-red-900/30 to-black border-2 border-red-600/50 rounded-lg p-6 animate-fadeIn animation-delay-1600 hover:border-red-600 transition-all duration-500 hover:shadow-lg hover:shadow-red-600/20 hover:scale-[1.01]">
                  <div className="text-gray-300 leading-relaxed space-y-4">
                    {thirdThird.map((paragraph, idx) => (
                      <p key={idx} className="animate-fadeIn" style={{ animationDelay: `${1800 + idx * 100}ms` }}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-8">
              {episode.game_thumbnail_460x215 && (
                <div className="relative group overflow-hidden rounded-lg border-4 border-yellow-400/50 shadow-2xl animate-fadeIn animation-delay-400 hover:border-yellow-400 transition-all duration-500 hover:shadow-yellow-400/50 hover:scale-[1.02]">
                  <Picture
                    storageRoot={storageRoot}
                    filename={episode.game_thumbnail_460x215}
                    alt={`${episode.title} game thumbnail`}
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 80vw, 60vw"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                    priority
                  />
                  {episode.game_link && (
                    <a
                      href={episode.game_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center"
                    >
                      <div className="text-center transform -translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                        <Gamepad2 className="text-yellow-400 mx-auto mb-2 animate-bounce" size={64} />
                        <span className="text-white font-bold text-2xl">Play Now</span>
                      </div>
                    </a>
                  )}
                </div>
              )}

              <div className="bg-gradient-to-br from-red-900/40 to-black border-2 border-red-600/50 rounded-lg p-6 animate-fadeIn animation-delay-600 hover:border-red-600 transition-all duration-500 hover:shadow-lg hover:shadow-red-600/20 hover:scale-[1.01]">
                <h3 className="text-yellow-400 font-bold text-xl mb-6 uppercase tracking-wider border-l-4 border-red-500 pl-4 hover:border-yellow-400 transition-colors duration-300">
                  Episode Info
                </h3>
                <div className="space-y-4 text-sm">
                  <div className="animate-fadeIn animation-delay-800">
                    <span className="text-gray-400 font-bold block mb-2">Episode</span>
                    <span className="text-white text-lg">{episode.podcast_number}</span>
                  </div>
                  {episode.game_project && (
                    <div className="animate-fadeIn animation-delay-900">
                      <span className="text-gray-400 font-bold block mb-2">Featured Game</span>
                      <span className="text-white">{episode.game_project}</span>
                    </div>
                  )}
                  {episode.guests && (
                    <div className="animate-fadeIn animation-delay-1000">
                      <span className="text-gray-400 font-bold block mb-2">Guests</span>
                      <span className="text-white">{episode.guests}</span>
                    </div>
                  )}
                  {episode.keywords && episode.keywords.length > 0 && (
                    <div className="animate-fadeIn animation-delay-1100">
                      <span className="text-gray-400 font-bold block mb-2">Tags</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {episode.keywords.map((keyword, idx) => (
                          <span
                            key={idx}
                            className="bg-yellow-400/90 text-black px-3 py-1 text-xs font-bold rounded hover:bg-yellow-400 transition-all duration-300 cursor-default hover:scale-110 hover:shadow-lg hover:shadow-yellow-400/50"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {episode.game_link && (
                    <div className="animate-fadeIn animation-delay-1200">
                      <span className="text-gray-400 font-bold block mb-2">Links</span>
                      <a
                        href={episode.game_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-400 hover:text-yellow-300 flex items-center gap-2 transition-all duration-300 hover:gap-4"
                      >
                        <ExternalLink size={14} />
                        Game Page
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {episode.screenshot_2_600x338 && (
                <div className="overflow-hidden rounded-lg border-2 border-red-500/50 animate-fadeIn animation-delay-1000 hover:border-red-500 transition-all duration-500 hover:shadow-lg hover:shadow-red-500/30 group">
                  <Picture
                    storageRoot={storageRoot}
                    filename={episode.screenshot_2_600x338}
                    alt="Screenshot 2"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              )}

              {secondThird.length > 0 && (
                <div className="relative">
                  <div className="bg-gradient-to-br from-red-900/30 to-black border-2 border-red-600/50 rounded-lg p-6 animate-fadeIn animation-delay-1200 hover:border-red-600 transition-all duration-500 hover:shadow-lg hover:shadow-red-600/20 hover:scale-[1.01]">
                    <div className="text-gray-300 leading-relaxed space-y-4">
                      {secondThird.map((paragraph, idx) => (
                        <p key={idx} className="animate-fadeIn" style={{ animationDelay: `${1400 + idx * 100}ms` }}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                  {thirdThird.length > 0 && (
                    <div className="flex justify-start my-4 lg:block hidden">
                      <div className="flex items-center gap-2 animate-pulse">
                        <ArrowDownLeft className="text-yellow-400 animate-bounce" size={32} />
                        <span className="text-yellow-400 text-xs font-bold tracking-wider">FINISH</span>
                      </div>
                    </div>
                  )}
                  {thirdThird.length > 0 && (
                    <div className="flex justify-center my-4 lg:hidden">
                      <div className="flex flex-col items-center gap-2 animate-bounce animation-delay-1000">
                        <ChevronDown className="text-yellow-400" size={32} />
                        <span className="text-yellow-400 text-xs font-bold tracking-wider">FINISH READING</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {episode.screenshot_3_600x338 && (
                <div className="overflow-hidden rounded-lg border-2 border-red-500/50 animate-fadeIn animation-delay-1800 hover:border-red-500 transition-all duration-500 hover:shadow-lg hover:shadow-red-500/30 group">
                  <Picture
                    storageRoot={storageRoot}
                    filename={episode.screenshot_3_600x338}
                    alt="Screenshot 3"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
